/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import { Product, Products, ProductTitle } from "components/Plans/Plan";
import { ProductFeature } from "components/Plans/ProductFeature";
import iconMicCheck from "components/Plans/images/mic-check-light.svg";
import styled from "styled-components";
import {
  IconAdjustStrength,
  IconBulkUpload,
  IconCrown,
  IconEnhanceMore,
  IconMode,
  IconVideo,
  IconDownload,
  IconSpeakers,
} from "components/Plans/IconComponent";
import clockIcon from "images/clock-icon.svg";
import iconSpeakersDark from "images/Studio/speakers-icon-dark.svg";
import iconAdjustStrengthDark from "components/Plans/images/adjust-strength-dark-for-upsell.svg";
import downloadImgDark from "images/EnhanceSpeech/download-icon-dark.svg";
import hideIcon from "images/VideoDownloadsPanel/hide.svg";
import colorPaletteIcon from "images/VideoDownloadsPanel/color-palette.svg";
import videoIcon from "images/VideoDownloadsPanel/video.svg";

export type ProductFeatureList =
  | typeof EnhancePremiumFeatureList
  | typeof MicCheckFeatureList
  | typeof StudioFeatureList
  | typeof ExpressPremiumFeatureList;

export const ProductList: React.FC<{
  products: ProductFeatureList[];
  mode?: IconMode;
  compact?: boolean;
}> = ({ products, mode = IconMode.light, compact = false }) => {
  return (
    <Products>
      {products.map((ProductFeatureList, index) => (
        <ProductFeatureList key={index} mode={mode} compact={compact} />
      ))}
    </Products>
  );
};

export const EnhancePremiumFeatureList: React.FC<{
  mode?: IconMode;
  compact?: boolean;
}> = ({ mode = IconMode.light, compact = true }) => {
  return (
    <Product>
      <ProductTitle>
        {I18n.t("plans.premium.enhance_speech.title")}
      </ProductTitle>
      <ProductFeature
        image={IconVideo}
        mode={mode}
        compact={compact}
        title={I18n.t("plans.premium.enhance_speech.video.title")}
        description={I18n.t("plans.premium.enhance_speech.video.subtitle")}
      />
      <ProductFeature
        image={IconBulkUpload}
        mode={mode}
        compact={compact}
        title={I18n.t("plans.premium.enhance_speech.bulk.title")}
        description={I18n.t("plans.premium.enhance_speech.bulk.subtitle")}
      />
      <ProductFeature
        image={IconAdjustStrength}
        mode={mode}
        compact={compact}
        title={I18n.t("plans.premium.enhance_speech.strength.title")}
        description={I18n.t("plans.premium.enhance_speech.strength.subtitle")}
      />
      <ProductFeature
        image={IconEnhanceMore}
        mode={mode}
        compact={compact}
        title={I18n.t("plans.premium.enhance_speech.max_duration.title")}
        description={I18n.t(
          "plans.premium.enhance_speech.max_duration.subtitle"
        )}
      />
    </Product>
  );
};

export const StudioFeatures: React.FC<{
  mode?: IconMode;
}> = ({ mode = IconMode.light }) => {
  return (
    <>
      <ProductFeature
        mode={mode}
        image={IconDownload}
        title={I18n.t("plans.premium.podcast_studio.no_download_limits")}
        description={I18n.t("plans.premium.podcast_studio.on_studio_projects")}
      />
      <ProductFeature
        mode={mode}
        image={IconSpeakers}
        title={I18n.t(
          "plans.premium.podcast_studio.download_original_recordings"
        )}
        description={I18n.t("plans.premium.podcast_studio.speaker_separated")}
      />
      <ProductFeature
        mode={mode}
        image={IconVideo}
        title={I18n.t("plans.premium.podcast_studio.download_audiograms")}
        description={I18n.t(
          "plans.premium.podcast_studio.without_podcast_branding"
        )}
      />
    </>
  );
};

export const StudioFeatureList: React.FC<{
  mode?: IconMode;
}> = ({ mode = IconMode.light }) => {
  return (
    <Product>
      <ProductTitle>
        {I18n.t("plans.premium.podcast_studio.title")}
      </ProductTitle>
      <StudioFeatures mode={mode} />
    </Product>
  );
};

// TODO: Support LIGHT and DARK mode
export const StudioDownloadLimitFeatureList: React.FC = () => {
  return (
    <Product>
      <ProductTitle>{I18n.t("pre_upsell_dialog.features")}</ProductTitle>
      <ProductFeature
        image={clockIcon}
        compact={true}
        description={I18n.t("pre_upsell_dialog.feature_time_limit")}
      />
      <ProductFeature
        image={downloadImgDark}
        compact={true}
        description={I18n.t("pre_upsell_dialog.feature_unlimited_downloads")}
      />
      <ProductFeature
        image={iconAdjustStrengthDark}
        compact={true}
        description={I18n.t("pre_upsell_dialog.feature_more")}
      />
    </Product>
  );
};

// TODO: Support LIGHT and DARK mode
// Used for the upsell modal from attempting to download original speaker tracks
export const StudioDownloadOriginalSpeakerTracksFeatureList: React.FC = () => {
  return (
    <Product>
      <ProductTitle>{I18n.t("pre_upsell_dialog.features")}</ProductTitle>
      <ProductFeature
        image={iconSpeakersDark}
        compact={true}
        description={I18n.t(
          "downloads_modal.original_recordings.upsell_modal.features.original_recording_sessions"
        )}
      />
      <ProductFeature
        image={iconAdjustStrengthDark}
        compact={true}
        description={I18n.t(
          "downloads_modal.original_recordings.upsell_modal.features.adjust_enhance_speech"
        )}
      />
      <ProductFeature
        image={downloadImgDark}
        compact={true}
        description={I18n.t(
          "downloads_modal.original_recordings.upsell_modal.features.no_download_limits"
        )}
      />
    </Product>
  );
};

export const StudioExportUpsellFeatureList: React.FC = () => {
  return (
    <Product>
      <ProductTitle>{I18n.t("pre_upsell_dialog.features")}</ProductTitle>
      <ProductFeature
        image={hideIcon}
        compact={true}
        description={I18n.t(
          "downloads_modal_video.upsell_modal.features.hide_branding"
        )}
      />
      <ProductFeature
        image={colorPaletteIcon}
        compact={true}
        description={I18n.t(
          "downloads_modal_video.upsell_modal.features.export_video_audiogram_themes"
        )}
      />
      <ProductFeature
        image={downloadImgDark}
        compact={true}
        description={I18n.t(
          "downloads_modal_video.upsell_modal.features.no_download_limits"
        )}
      />
      <ProductFeature
        image={iconSpeakersDark}
        compact={true}
        description={I18n.t(
          "downloads_modal_video.upsell_modal.features.download_original_recordings"
        )}
      />
      <ProductFeature
        image={videoIcon}
        compact={true}
        description={I18n.t(
          "downloads_modal_video.upsell_modal.features.download_video_project"
        )}
      />
    </Product>
  );
};

// TODO: Support LIGHT and DARK mode
export const MicCheckFeatureList: React.FC = () => {
  return (
    <Product>
      <ProductTitle>{I18n.t("plans.premium.mic_check.title")}</ProductTitle>
      <ProductFeature
        image={iconMicCheck}
        title={I18n.t("plans.premium.mic_check.analyze")}
        description={I18n.t("plans.premium.mic_check.description")}
      />
    </Product>
  );
};

export const ExpressPremiumFeatureList: React.FC<{ mode?: IconMode }> = ({
  mode = IconMode.light,
}) => {
  return (
    <Product>
      <ProductTitle>{I18n.t("plans.premium.express.title")}</ProductTitle>
      <ProductFeature image={IconCrown} mode={mode}>
        <Emphasis>{I18n.t("plans.premium.express.features.title")}</Emphasis>
        &nbsp;
        {I18n.t("plans.premium.express.features.subtitle")}
      </ProductFeature>
    </Product>
  );
};

const Emphasis = styled.span`
  font-weight: 700;
`;
